import { useState, FormEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import axios from 'axios';
import { setAuth } from '../utils/auth';
import type { AuthData } from '../types';
import { palette, typography, theme } from '../theme/colors';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data } = await axios.post<AuthData>(`${API_URL}/auth/login`, {
        email,
        password,
      });

      // Check if user is platform staff (can access store-admin)
      // Customers (userType === 'customer') cannot access store-admin
      const isPlatformStaff = data.user.userType === 'platform_staff';
      const isSuperAdmin = data.user.isSuperAdmin;

      // For backward compatibility during migration, also check roles
      const allowedRoles = ['org_owner', 'org_admin', 'store_owner', 'store_manager', 'store_staff', 'warehouse_manager', 'driver'];
      const hasStaffRole =
        data.user.roleSystemKeys?.some(key => allowedRoles.includes(key)) ||
        data.user.roles?.some(role => allowedRoles.includes(role.systemKey || ''));

      // User can access if:
      // 1. They are platform_staff userType
      // 2. OR they are super admin
      // 3. OR they have a staff role (backward compatibility before migration)
      const canAccessAdmin = isPlatformStaff || isSuperAdmin || hasStaffRole;

      if (data.access_token && canAccessAdmin) {
        setAuth(data);
        navigate('/app');
      } else {
        setError('Access denied. This portal is for store staff only. Customers should use the storefront.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <TrendingUpOutlinedIcon />, title: 'Real-time Analytics', desc: 'Track sales and performance' },
    { icon: <InventoryOutlinedIcon />, title: 'Inventory Control', desc: 'Manage stock efficiently' },
    { icon: <AnalyticsOutlinedIcon />, title: 'Smart Reports', desc: 'Data-driven insights' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: palette.background,
      }}
    >
      {/* Left Side - Branding Panel */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          width: '50%',
          position: 'relative',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: palette.background,
          overflow: 'hidden',
          p: 6,
        }}
      >
        {/* Background Gradient Mesh */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: theme.gradients.mesh,
            opacity: 0.8,
          }}
        />

        {/* Animated Gradient Orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '20%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${palette.primary}25 0%, transparent 70%)`,
            filter: 'blur(80px)',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0)' },
              '50%': { transform: 'translate(30px, -30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '15%',
            width: 350,
            height: 350,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${palette.accent}20 0%, transparent 70%)`,
            filter: 'blur(60px)',
            animation: 'float2 10s ease-in-out infinite',
            '@keyframes float2': {
              '0%, 100%': { transform: 'translate(0, 0)' },
              '50%': { transform: 'translate(-20px, 20px)' },
            },
          }}
        />

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(${palette.border}15 1px, transparent 1px),
              linear-gradient(90deg, ${palette.border}15 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            opacity: 0.5,
          }}
        />

        {/* Content */}
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 480 }}>
          {/* Logo */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: theme.radius['2xl'],
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
            }}
          >
            <StorefrontIcon sx={{ color: palette.textOnPrimary, fontSize: 40 }} />
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
              mb: 2,
              fontSize: { lg: '2.5rem', xl: '3rem' },
              letterSpacing: typography.letterSpacing.tight,
            }}
          >
            Nexera Plateforme
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: palette.textSecondary,
              mb: 5,
              lineHeight: 1.8,
              fontSize: typography.fontSize.lg,
            }}
          >
            Manage your inventory, track sales, process reservations, and grow your business with powerful analytics.
          </Typography>

          {/* Feature Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {features.map((feature, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: theme.radius.lg,
                  backgroundColor: palette.surfaceElevated,
                  border: `1px solid ${palette.border}`,
                  transition: theme.transitions.normal,
                  '&:hover': {
                    borderColor: palette.borderLight,
                    transform: 'translateX(8px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: theme.radius.lg,
                    backgroundColor: palette.primaryMuted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: palette.primary,
                  }}
                >
                  {feature.icon}
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: typography.fontWeight.semibold,
                      color: palette.textPrimary,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: palette.textMuted }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Version Badge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 32,
            left: 32,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: typography.fontFamily.mono,
              color: palette.textMuted,
              fontSize: '11px',
              letterSpacing: typography.letterSpacing.wider,
            }}
          >
            NEXERA v2.0
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, sm: 6 },
          backgroundColor: palette.surface,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile Logo */}
          <Box
            sx={{
              display: { xs: 'flex', lg: 'none' },
              alignItems: 'center',
              gap: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: theme.radius.lg,
                background: theme.gradients.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorefrontIcon sx={{ color: palette.textOnPrimary, fontSize: 24 }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: palette.textPrimary,
              }}
            >
              Nexera
            </Typography>
          </Box>

          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              mb: 1,
              display: 'block',
            }}
          >
            Welcome Back
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
              mb: 1,
            }}
          >
            Sign in to your account
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: palette.textSecondary, mb: 4 }}
          >
            Enter your credentials to access the dashboard
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: theme.radius.lg,
                backgroundColor: palette.errorMuted,
                color: palette.error,
                border: `1px solid ${palette.error}30`,
                '& .MuiAlert-icon': {
                  color: palette.error,
                },
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: palette.textSecondary,
                  fontWeight: typography.fontWeight.medium,
                  mb: 1,
                  display: 'block',
                }}
              >
                Email Address
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: palette.surfaceElevated,
                    borderRadius: theme.radius.lg,
                    '& fieldset': {
                      borderColor: palette.border,
                    },
                    '&:hover fieldset': {
                      borderColor: palette.borderLight,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: palette.textPrimary,
                    '&::placeholder': {
                      color: palette.textMuted,
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: palette.textSecondary,
                  fontWeight: typography.fontWeight.medium,
                  mb: 1,
                  display: 'block',
                }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: palette.textMuted }}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: palette.surfaceElevated,
                    borderRadius: theme.radius.lg,
                    '& fieldset': {
                      borderColor: palette.border,
                    },
                    '&:hover fieldset': {
                      borderColor: palette.borderLight,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: palette.textPrimary,
                    '&::placeholder': {
                      color: palette.textMuted,
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  color: palette.primary,
                  textDecoration: 'none',
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              endIcon={!loading && <ArrowForwardIcon />}
              sx={{
                py: 1.75,
                background: theme.gradients.primary,
                color: palette.textOnPrimary,
                fontWeight: typography.fontWeight.semibold,
                fontSize: typography.fontSize.md,
                borderRadius: theme.radius.lg,
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: palette.border,
                  color: palette.textMuted,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: palette.textOnPrimary }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

        </Box>
      </Box>
    </Box>
  );
};
